import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Edit, Trash2, Copy, GripVertical, Settings, X } from 'lucide-react';
import { Soundworld } from '../../contexts/SoundworldsContext';
import { BuildProgressionModal } from './BuildProgressionModal';

const ROLE_ORDER = [
    'MELODY', 'LEAD',
    'CHORDS', 'OSTINATO',
    'TEXTURE',
    'BASS',
    'DRUM_KIT', 'DRUMS',
    'HIHAT', 'RIDE', 'CRASH',
    'TOMS', 'DRUMS_OTHER',
    'SNARE',
    'KICK',
    'PERCUSSION',
    'RISER', 'TRANSITION',
    'TAG', 'WATERMARK'
];

const normalizeKey = (value: string) => value.replace(/[^a-zA-Z]/g, '').toLowerCase();
const ORDER_NORMALIZED = ROLE_ORDER.map(normalizeKey);
const getRolePriority = (role: string) => {
    const n = normalizeKey(role);
    const idx = ORDER_NORMALIZED.indexOf(n);
    if (idx !== -1) return idx;
    for (let i = 0; i < ORDER_NORMALIZED.length; i++) {
        const key = ORDER_NORMALIZED[i];
        if (n.includes(key) || key.includes(n)) return i;
    }
    return Number.POSITIVE_INFINITY;
};

const ROLE_GROUPS: Record<string, string[]> = {
    LEAD_MELODY: ['LEAD', 'MELODY'],
    CHORDS_OSTINATO: ['CHORDS', 'OSTINATO'],
    TEXTURE: ['TEXTURE'],
    BASS: ['BASS'],
    DRUM_KIT_DRUMS: ['DRUM_KIT', 'DRUMS'],
    HIHAT_RIDE_CRASH: ['HIHAT', 'RIDE', 'CRASH'],
    TOMS_DRUMS_OTHER: ['TOMS', 'DRUMS_OTHER'],
    SNARE: ['SNARE'],
    KICK: ['KICK'],
    PERCUSSION: ['PERCUSSION'],
    RISER_TRANSITION: ['RISER', 'TRANSITION'],
    TAG_WATERMARK: ['TAG', 'WATERMARK'],
};

const ROLE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    LEAD_MELODY: { bg: '#E0FCED', border: '#CBF7E1', text: '#0B835A' },
    CHORDS_OSTINATO: { bg: '#DDF7FF', border: '#D0EFF7', text: '#0F738D' },
    TEXTURE: { bg: '#F5F0FF', border: '#E8DDFE', text: '#6B44BA' },
    BASS: { bg: '#E5EEFF', border: '#D6E5FF', text: '#245DC5' },
    DRUM_KIT_DRUMS: { bg: '#F5F0FF', border: '#E8DDFE', text: '#6B44BA' },
    HIHAT_RIDE_CRASH: { bg: '#F4FBCB', border: '#E9F797', text: '#566604' },
    TOMS_DRUMS_OTHER: { bg: '#FFF3CC', border: '#FFE59E', text: '#8D540A' },
    SNARE: { bg: '#FEEEE1', border: '#FFE59E', text: '#874D00' },
    KICK: { bg: '#FFEBEB', border: '#FFDCDB', text: '#C03636' },
    PERCUSSION: { bg: '#FEECF3', border: '#FDDDEA', text: '#B81C5D' },
    RISER_TRANSITION: { bg: '#FDEDFF', border: '#FAD6FF', text: '#A024AF' },
    TAG_WATERMARK: { bg: '#F8F9FA', border: '#EEEFF1', text: '#505154' },
};

const resolveRoleGroupKey = (role: string): string | null => {
    const upper = role.toUpperCase();
    for (const [groupKey, roles] of Object.entries(ROLE_GROUPS)) {
        if (roles.includes(upper)) return groupKey;
    }
    const rNorm = normalizeKey(upper);
    for (const [groupKey, roles] of Object.entries(ROLE_GROUPS)) {
        for (const rr of roles) {
            const rrNorm = normalizeKey(rr);
            if (rNorm.includes(rrNorm) || rrNorm.includes(rNorm)) return groupKey;
        }
    }
    return null;
};

const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val));
const hexToRgb = (hex: string) => {
    const m = hex.replace('#', '');
    const bigint = parseInt(m, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
};
const rgbToHex = (r: number, g: number, b: number) =>
    '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
const shadeColor = (hex: string, percent: number) => {
    const { r, g, b } = hexToRgb(hex);
    const factor = 1 + percent / 100;
    const nr = clamp(Math.round(r * factor), 0, 255);
    const ng = clamp(Math.round(g * factor), 0, 255);
    const nb = clamp(Math.round(b * factor), 0, 255);
    return rgbToHex(nr, ng, nb);
};

const buildRoleCSS = () => {
    let css = '';
    for (const [groupKey, colors] of Object.entries(ROLE_COLORS)) {
        const baseBg = colors.bg;
        const baseBorder = colors.border;
        const baseText = colors.text;
        const hoverBg = shadeColor(baseBg, -8);
        const hoverBorder = shadeColor(baseBorder, -8);
        const activeBg = shadeColor(baseBg, -18);
        const activeBorder = shadeColor(baseBorder, -18);
        const selectedBg = shadeColor(baseBg, -22);
        const selectedBorder = shadeColor(baseBorder, -22);

        const darkBg = shadeColor(baseBg, -20);
        const darkBorder = shadeColor(baseBorder, -16);
        const darkHoverBg = shadeColor(baseBg, -26);
        const darkHoverBorder = shadeColor(baseBorder, -22);
        const darkActiveBg = shadeColor(baseBg, -34);
        const darkActiveBorder = shadeColor(baseBorder, -30);
        const darkSelectedBg = shadeColor(baseBg, -38);
        const darkSelectedBorder = shadeColor(baseBorder, -34);

        css += `
.role-btn-${groupKey} { background-color: ${baseBg}; border-color: ${baseBorder}; color: ${baseText}; }
.role-btn-${groupKey}:hover { background-color: ${hoverBg}; border-color: ${hoverBorder}; }
.role-btn-${groupKey}:active { background-color: ${activeBg}; border-color: ${activeBorder}; }
.role-btn-${groupKey}.is-selected { background-color: ${selectedBg}; border-color: ${selectedBorder}; box-shadow: 0 0 0 2px ${selectedBorder} inset; }
.dark .role-btn-${groupKey} { background-color: ${darkBg}; border-color: ${darkBorder}; color: ${baseText}; }
.dark .role-btn-${groupKey}:hover { background-color: ${darkHoverBg}; border-color: ${darkHoverBorder}; }
.dark .role-btn-${groupKey}:active { background-color: ${darkActiveBg}; border-color: ${darkActiveBorder}; }
.dark .role-btn-${groupKey}.is-selected { background-color: ${darkSelectedBg}; border-color: ${darkSelectedBorder}; box-shadow: 0 0 0 2px ${darkSelectedBorder} inset; }
`;
    }
    return css;
};

interface BarData {
    id: string;
    name: string;
    bars: number;
    chords: string;
    elements: string[];
}

interface VerticalSongStructureProps {
    soundworld?: Soundworld;
    initialFormData?: Array<{
        name: string;
        bars: number;
        chords?: string;
        elements: string[];
    }>;
    visibleElements?: Set<string>;
    onVisibleElementsChange?: (elements: Set<string>) => void;
    onFormDataChange: (formData: {
        soundworlds: string[];
        tempo: string;
        chords: string;
        form: Array<{
            name: string;
            bars: number;
            chords?: string;
            elements: string[];
        }>;
    }) => void;
}

export const VerticalSongStructure: React.FC<VerticalSongStructureProps> = ({
    soundworld,
    initialFormData,
    visibleElements: externalVisibleElements,
    onVisibleElementsChange,
    onFormDataChange
}) => {
    const [bars, setBars] = useState<BarData[]>([
        { id: '1', name: 'Section 1', bars: 8, chords: '', elements: [] }
    ]);
    const [editingBarId, setEditingBarId] = useState<string | null>(null);
    const [editingBarName, setEditingBarName] = useState('');
    const [isProgressionModalOpen, setIsProgressionModalOpen] = useState<string | null>(null);
    const [isElementsSettingsModalOpen, setIsElementsSettingsModalOpen] = useState(false);
    const [internalVisibleElements, setInternalVisibleElements] = useState<Set<string>>(new Set());
    const visibleElements = externalVisibleElements ?? internalVisibleElements;
    const setVisibleElements = onVisibleElementsChange ?? setInternalVisibleElements;
    const [tempVisibleElements, setTempVisibleElements] = useState<Set<string>>(new Set());
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const sendFormData = useCallback(() => {
        const form = bars.map(bar => ({
            name: bar.name,
            bars: bar.bars,
            chords: bar.chords || undefined,
            elements: bar.elements
        }));

        onFormDataChange({
            soundworlds: soundworld ? [soundworld.name] : [],
            tempo: '120',
            chords: '',
            form: form
        });
    }, [bars, soundworld, onFormDataChange]);

    const prevInitialFormDataRef = useRef<string>('');
    const isInitializingRef = useRef(false);

    useEffect(() => {
        if (!initialFormData) {
            if (prevInitialFormDataRef.current !== '') {
                prevInitialFormDataRef.current = '';
                const defaultBars = [{ id: '1', name: 'Section 1', bars: 8, chords: '', elements: [] }];
                isInitializingRef.current = true;
                setBars(defaultBars);
                setTimeout(() => {
                    isInitializingRef.current = false;
                }, 0);
            }
            return;
        }

        const currentDataString = JSON.stringify(initialFormData);
        
        if (currentDataString !== prevInitialFormDataRef.current) {
            prevInitialFormDataRef.current = currentDataString;
            isInitializingRef.current = true;
            
            if (initialFormData.length > 0) {
                const newBars: BarData[] = initialFormData.map((item, index) => ({
                    id: (index + 1).toString(),
                    name: item.name || `Section ${index + 1}`,
                    bars: item.bars || 8,
                    chords: item.chords || '',
                    elements: item.elements || []
                }));
                setBars(newBars);
                
                const selectedElementsSet = new Set<string>();
                initialFormData.forEach(item => {
                    if (item.elements && item.elements.length > 0) {
                        item.elements.forEach(element => selectedElementsSet.add(element));
                    }
                });
                
                if (selectedElementsSet.size > 0) {
                    setVisibleElements(prev => {
                        const newSet = new Set(prev);
                        let hasChanges = false;
                        selectedElementsSet.forEach(element => {
                            if (!newSet.has(element)) {
                                newSet.add(element);
                                hasChanges = true;
                            }
                        });
                        return hasChanges ? newSet : prev;
                    });
                }
            } else {
                const defaultBars = [{ id: '1', name: 'Section 1', bars: 8, chords: '', elements: [] }];
                setBars(defaultBars);
            }
            
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 0);
        }
    }, [initialFormData]);

    const prevBarsRef = useRef<string>('');

    useEffect(() => {
        if (!isInitializingRef.current) {
            const barsString = JSON.stringify(bars);
            if (barsString !== prevBarsRef.current) {
                prevBarsRef.current = barsString;
                sendFormData();
            }
        } else {
            prevBarsRef.current = JSON.stringify(bars);
        }
    }, [bars, sendFormData]);

    const addBar = () => {
        const newId = (bars.length + 1).toString();
        const newBar: BarData = {
            id: newId,
            name: `Section ${newId}`,
            bars: 8,
            chords: '',
            elements: []
        };
        setBars([...bars, newBar]);
    };

    const deleteBar = (id: string) => {
        if (bars.length <= 1) return;
        setBars(bars.filter(b => b.id !== id));
    };

    const duplicateBar = (id: string) => {
        const bar = bars.find(b => b.id === id);
        if (!bar) return;
        
        const barIndex = bars.findIndex(b => b.id === id);
        const newId = (bars.length + 1).toString();
        const newBar: BarData = {
            id: newId,
            name: `${bar.name} (Copy)`,
            bars: bar.bars,
            chords: bar.chords,
            elements: [...bar.elements]
        };
        
        const newBars = [...bars];
        newBars.splice(barIndex + 1, 0, newBar);
        setBars(newBars);
    };

    const updateBarName = (id: string, newName: string) => {
        setBars(bars.map(bar => bar.id === id ? { ...bar, name: newName } : bar));
    };

    const updateBarBars = (id: string, newBars: number) => {
        setBars(bars.map(bar => bar.id === id ? { ...bar, bars: newBars } : bar));
    };

    const updateBarChords = (id: string, newChords: string) => {
        setBars(bars.map(bar => bar.id === id ? { ...bar, chords: newChords } : bar));
    };

    const handleElementToggle = (barId: string, elementId: string) => {
        setBars(bars.map(bar => {
            if (bar.id === barId) {
                const newElements = bar.elements.includes(elementId)
                    ? bar.elements.filter(id => id !== elementId)
                    : [...bar.elements, elementId];
                return { ...bar, elements: newElements };
            }
            return bar;
        }));
    };

    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDraggedId(id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, id: string) => {
        e.preventDefault();
        if (draggedId && draggedId !== id) {
            setDragOverId(id);
        }
    };

    const handleDragLeave = () => {
        setDragOverId(null);
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedId || draggedId === targetId) return;

        const draggedIndex = bars.findIndex(b => b.id === draggedId);
        const targetIndex = bars.findIndex(b => b.id === targetId);

        const newBars = [...bars];
        const [draggedBar] = newBars.splice(draggedIndex, 1);
        newBars.splice(targetIndex, 0, draggedBar);

        setBars(newBars);
        setDraggedId(null);
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverId(null);
    };

    const getElementStyles = (elementId: string) => {
        if (!soundworld?.elements) return undefined;
        const element = soundworld.elements[elementId];
        if (!element) return undefined;
        const role = (element as any)?.role || elementId;
        const groupKey = resolveRoleGroupKey(role);
        return groupKey ? ROLE_COLORS[groupKey] : undefined;
    };

    const sortedElements = soundworld?.elements && typeof soundworld.elements === 'object'
        ? Object.entries(soundworld.elements).sort(([roleA, elementA], [roleB, elementB]) => {
            const effectiveRoleA = (elementA as any)?.role ?? roleA;
            const effectiveRoleB = (elementB as any)?.role ?? roleB;
            const a = getRolePriority(effectiveRoleA);
            const b = getRolePriority(effectiveRoleB);
            if (a !== b) return a - b;
            return String(effectiveRoleA).localeCompare(String(effectiveRoleB)) || roleA.localeCompare(roleB);
        })
        : [];

    const getDefaultVisibleElements = useCallback((elements: Array<[string, any]>) => {
        const defaultVisible = new Set<string>();
        const seenGroups = new Set<string>();
        let hasUngrouped = false;
        
        elements.forEach(([role, element]) => {
            const effectiveRole = (element as any)?.role ?? role;
            const groupKey = resolveRoleGroupKey(effectiveRole);
            if (groupKey && !seenGroups.has(groupKey)) {
                defaultVisible.add(role);
                seenGroups.add(groupKey);
            } else if (!groupKey && !hasUngrouped) {
                defaultVisible.add(role);
                hasUngrouped = true;
            }
        });
        
        return defaultVisible;
    }, []);

    useEffect(() => {
        if (sortedElements.length > 0 && visibleElements.size === 0) {
            const defaultVisible = getDefaultVisibleElements(sortedElements);
            setVisibleElements(defaultVisible);
        }
    }, [sortedElements, visibleElements.size, getDefaultVisibleElements, setVisibleElements]);

    const filteredElements = useMemo(() => {
        if (visibleElements.size === 0) {
            return sortedElements;
        }
        return sortedElements.filter(([role]) => visibleElements.has(role));
    }, [sortedElements, visibleElements]);

    return (
        <div className="flex gap-6 min-w-0">
            <style>{buildRoleCSS()}
                {`
                    .bar-header-separator::before {
                        content: '';
                        position: absolute;
                        left: -16px;
                        bottom: -16px;
                        width: 1px;
                        height: calc(50% + 16px);
                        background-color: hsl(var(--border));
                        z-index: 20;
                    }
                    .elements-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: hsl(var(--border)) hsl(var(--muted));
                    }
                    .elements-scrollbar::-webkit-scrollbar {
                        width: 8px;
                        display: block;
                    }
                    .elements-scrollbar::-webkit-scrollbar-track {
                        background: hsl(var(--muted));
                        border-radius: 4px;
                    }
                    .elements-scrollbar::-webkit-scrollbar-thumb {
                        background-color: hsl(var(--border));
                        border-radius: 4px;
                    }
                    .elements-scrollbar::-webkit-scrollbar-thumb:hover {
                        background-color: hsl(var(--muted-foreground) / 0.5);
                    }
                `}
            </style>
            
            <div className="w-64 flex-shrink-0">
                <div className="flex flex-col gap-2 pt-[102px]">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-sm text-card-foreground">Elements</h4>
                        <button
                            onClick={() => {
                                setTempVisibleElements(new Set(visibleElements));
                                setIsElementsSettingsModalOpen(true);
                            }}
                            className="text-muted-foreground hover:text-card-foreground transition-colors"
                            title="Elements settings"
                        >
                            <Settings size={16} />
                        </button>
                    </div>
                    {filteredElements.map(([role, element]: [string, any]) => {
                        const effectiveRole = (element as any)?.role ?? role;
                        const groupKey = resolveRoleGroupKey(effectiveRole);
                        const styles = getElementStyles(role);
                        return (
                            <div
                                key={role}
                                className={`h-10 px-3 rounded text-sm border transition-colors flex items-center ${
                                    groupKey ? `role-btn-${groupKey}` : 'bg-muted border-border text-card-foreground'
                                }`}
                                style={styles ? {
                                    backgroundColor: styles.bg,
                                    borderColor: styles.border,
                                    color: styles.text
                                } : undefined}
                            >
                                {element.name_pretty || role}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 w-0 overflow-x-auto elements-scrollbar">
                <div className="flex flex-col gap-4" style={{ width: 'max-content' }}>
                    <div className="flex gap-4 pb-4 relative" style={{ width: 'max-content' }}>
                        <div 
                            className="absolute bottom-0 left-0 h-px bg-border z-10"
                            style={{ width: `${bars.length * 300 + (bars.length - 1) * 16}px` }}
                        ></div>
                        {bars.map((bar, index) => (
                            <div
                                key={`header-${bar.id}`}
                                className={`w-[300px] flex-shrink-0 flex items-center justify-between relative z-10 ${index > 0 ? 'bar-header-separator' : ''} ${dragOverId === bar.id ? 'ring-2 ring-primary' : ''}`}
                                onDragOver={(e) => handleDragOver(e, bar.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, bar.id)}
                            >
                                {editingBarId === bar.id ? (
                                    <input
                                        type="text"
                                        value={editingBarName}
                                        onChange={(e) => setEditingBarName(e.target.value)}
                                        onBlur={() => {
                                            if (editingBarName.trim()) {
                                                updateBarName(bar.id, editingBarName.trim());
                                            }
                                            setEditingBarId(null);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                if (editingBarName.trim()) {
                                                    updateBarName(bar.id, editingBarName.trim());
                                                }
                                                setEditingBarId(null);
                                            } else if (e.key === 'Escape') {
                                                setEditingBarId(null);
                                            }
                                        }}
                                        className="font-semibold text-sm bg-transparent border border-border rounded px-2 py-1 focus:outline-none text-card-foreground min-w-0"
                                        autoFocus
                                    />
                                ) : (
                                    <h3
                                        className="font-semibold text-sm cursor-pointer hover:bg-accent px-2 py-1 rounded text-card-foreground"
                                        onClick={() => {
                                            setEditingBarName(bar.name);
                                            setEditingBarId(bar.id);
                                        }}
                                    >
                                        {bar.name}
                                    </h3>
                                )}
                                <div className="flex items-center gap-2 mr-4">
                                    <div
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, bar.id)}
                                        onDragEnd={handleDragEnd}
                                        className={`cursor-grab active:cursor-grabbing text-muted-foreground hover:text-card-foreground transition-colors ${draggedId === bar.id ? 'text-primary' : ''}`}
                                        title="Drag to reorder"
                                    >
                                        <GripVertical size={16} />
                                    </div>
                                    <button
                                        onClick={() => duplicateBar(bar.id)}
                                        className="text-muted-foreground hover:text-card-foreground"
                                        title="Duplicate section"
                                    >
                                        <Copy size={14} />
                                    </button>
                                    <button
                                        onClick={() => deleteBar(bar.id)}
                                        className="text-muted-foreground hover:text-destructive"
                                        title="Delete section"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="flex gap-4 items-stretch" style={{ width: 'max-content' }}>
                        {bars.map((bar) => (
                            <div
                                key={bar.id}
                                className={`w-[300px] flex-shrink-0 rounded-lg p-4 bg-card flex flex-col transition-all ${draggedId === bar.id ? 'opacity-50' : ''} ${dragOverId === bar.id ? 'ring-2 ring-primary border-primary' : ''}`}
                                onDragOver={(e) => handleDragOver(e, bar.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, bar.id)}
                            >
                                <div className="mb-4 flex-shrink-0">
                                <div className="flex gap-2 items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-card-foreground">Bars:</label>
                                        <input
                                            type="number"
                                            value={bar.bars}
                                            onChange={(e) => updateBarBars(bar.id, parseInt(e.target.value) || 8)}
                                            min="1"
                                            max="32"
                                            className="w-16 px-2 py-1 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary text-center bg-background text-card-foreground"
                                        />
                                    </div>
                                    
                                    <div className="flex items-center gap-2 flex-1 max-w-[200px]">
                                        <label className="text-sm font-medium text-card-foreground">Chords:</label>
                                        <div className="relative flex-1">
                                            <input
                                                type="text"
                                                value={bar.chords}
                                                onChange={(e) => updateBarChords(bar.id, e.target.value)}
                                                className="w-full px-3 py-1 pr-8 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-card-foreground"
                                                placeholder="Type your chord progression"
                                            />
                                            <button
                                                onClick={() => setIsProgressionModalOpen(bar.id)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                                                title="Open progression editor"
                                            >
                                                <Edit size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                </div>

                                <div className="flex-1 flex flex-col gap-2">
                                    {filteredElements.map(([role, element]: [string, any]) => {
                                        const isSelected = bar.elements.includes(role);
                                        const effectiveRole = (element as any)?.role ?? role;
                                        const groupKey = resolveRoleGroupKey(effectiveRole);
                                        const styles = getElementStyles(role);
                                        return (
                                            <button
                                                key={role}
                                                onClick={() => handleElementToggle(bar.id, role)}
                                                className={`h-10 rounded border transition-colors ${
                                                    isSelected ? 'is-selected' : 'bg-muted border-border'
                                                } ${
                                                    groupKey && isSelected ? `role-btn-${groupKey}` : ''
                                                }`}
                                                style={isSelected && styles ? {
                                                    backgroundColor: styles.bg,
                                                    borderColor: styles.border
                                                } : undefined}
                                                title={element.name_pretty || role}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    
                    <button
                        onClick={addBar}
                        className="w-64 flex-shrink-0 border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground hover:border-primary hover:text-card-foreground transition-colors flex flex-col items-center justify-center"
                    >
                        <Plus size={20} className="mb-2" />
                        <span className="text-sm font-medium">Add Section</span>
                    </button>
                    </div>
                </div>
            </div>

            {isProgressionModalOpen && (
                <BuildProgressionModal
                    isOpen={!!isProgressionModalOpen}
                    onClose={() => setIsProgressionModalOpen(null)}
                    initialChords={bars.find(b => b.id === isProgressionModalOpen)?.chords || ''}
                    onSave={(newChords) => {
                        if (isProgressionModalOpen) {
                            updateBarChords(isProgressionModalOpen, newChords);
                        }
                        setIsProgressionModalOpen(null);
                    }}
                />
            )}

            {isElementsSettingsModalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={() => {
                        setTempVisibleElements(new Set(visibleElements));
                        setIsElementsSettingsModalOpen(false);
                    }}
                >
                    <div
                        className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6 flex-shrink-0">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-semibold text-card-foreground">Elements Settings</h2>
                                <button
                                    onClick={() => {
                                        setTempVisibleElements(new Set(visibleElements));
                                        setIsElementsSettingsModalOpen(false);
                                    }}
                                    className="text-muted-foreground hover:text-card-foreground"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6">
                            <div className="space-y-4 pb-4">
                                {Object.entries(ROLE_GROUPS).map(([groupKey, roles]) => {
                                    const groupElements = sortedElements.filter(([role, element]) => {
                                        const effectiveRole = (element as any)?.role ?? role;
                                        const elementGroupKey = resolveRoleGroupKey(effectiveRole);
                                        return elementGroupKey === groupKey;
                                    });

                                    if (groupElements.length === 0) return null;

                                    const styles = ROLE_COLORS[groupKey];

                                    return (
                                        <div key={groupKey} className="space-y-2">
                                            <div
                                                className="px-3 py-2 rounded text-sm font-medium border"
                                                style={styles ? {
                                                    backgroundColor: styles.bg,
                                                    borderColor: styles.border,
                                                    color: styles.text
                                                } : undefined}
                                            >
                                                {groupKey.replace(/_/g, ' ')}
                                            </div>
                                            <div className="space-y-2 pl-4">
                                                {groupElements.map(([role, element]: [string, any]) => {
                                                    const isVisible = tempVisibleElements.has(role);
                                                    return (
                                                        <label
                                                            key={role}
                                                            className="flex items-center gap-2 cursor-pointer hover:bg-accent p-2 rounded"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isVisible}
                                                                onChange={(e) => {
                                                                    const newSet = new Set(tempVisibleElements);
                                                                    if (e.target.checked) {
                                                                        newSet.add(role);
                                                                    } else {
                                                                        newSet.delete(role);
                                                                    }
                                                                    setTempVisibleElements(newSet);
                                                                }}
                                                                className="w-4 h-4 rounded border-border"
                                                            />
                                                            <span className="text-sm text-card-foreground">
                                                                {element.name_pretty || role}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}

                                {(() => {
                                    const ungroupedElements = sortedElements.filter(([role, element]) => {
                                        const effectiveRole = (element as any)?.role ?? role;
                                        const elementGroupKey = resolveRoleGroupKey(effectiveRole);
                                        return !elementGroupKey;
                                    });

                                    if (ungroupedElements.length === 0) return null;

                                    return (
                                        <div className="space-y-2">
                                            <div className="px-3 py-2 rounded text-sm font-medium border bg-muted border-border text-card-foreground">
                                                Other
                                            </div>
                                            <div className="space-y-2 pl-4">
                                                {ungroupedElements.map(([role, element]: [string, any]) => {
                                                    const isVisible = tempVisibleElements.has(role);
                                                    return (
                                                        <label
                                                            key={role}
                                                            className="flex items-center gap-2 cursor-pointer hover:bg-accent p-2 rounded"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isVisible}
                                                                onChange={(e) => {
                                                                    const newSet = new Set(tempVisibleElements);
                                                                    if (e.target.checked) {
                                                                        newSet.add(role);
                                                                    } else {
                                                                        newSet.delete(role);
                                                                    }
                                                                    setTempVisibleElements(newSet);
                                                                }}
                                                                className="w-4 h-4 rounded border-border"
                                                            />
                                                            <span className="text-sm text-card-foreground">
                                                                {element.name_pretty || role}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className="p-6 pt-4 border-t border-border flex-shrink-0">
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setTempVisibleElements(new Set(visibleElements));
                                        setIsElementsSettingsModalOpen(false);
                                    }}
                                    className="px-4 py-2 text-sm border border-border rounded-md text-card-foreground hover:bg-accent transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setVisibleElements(new Set(tempVisibleElements));
                                        setIsElementsSettingsModalOpen(false);
                                    }}
                                    className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

